import React, { useCallback, useState } from 'react';
import StatusIndicator from '../../../components/common/StatusIndicator';
import Tooltip from '../../../components/common/Tooltip';
import { StudentTokenService } from '../../../services';
import {
  buildAnamneseUrl,
  buildEnviarFotosUrl,
  buildParqUrl,
} from '../../../utils/studentPublicLinks';
import styles from '../Onboarding.module.css';

export type OnboardingFormLinkTarget = 'anamnese' | 'parq' | 'enviar-fotos';

const LINK_BUILDERS: Record<OnboardingFormLinkTarget, (token: string) => string> = {
  anamnese: buildAnamneseUrl,
  parq: buildParqUrl,
  'enviar-fotos': buildEnviarFotosUrl,
};

const COPY_ERROR_MESSAGES: Record<OnboardingFormLinkTarget, string> = {
  anamnese: 'Não foi possível copiar o link de anamnese.',
  parq: 'Não foi possível copiar o link de Par-Q.',
  'enviar-fotos': 'Não foi possível copiar o link de envio de fotos.',
};

interface OnboardingFormStatusCellProps {
  active: boolean;
  activeLabel: string;
  missingLabel: string;
  studentId: number | null;
  linkTarget: OnboardingFormLinkTarget;
}

const HOVER_HINT = 'Ao clicar, o link será copiado';

const OnboardingFormStatusCell: React.FC<OnboardingFormStatusCellProps> = ({
  active,
  activeLabel,
  missingLabel,
  studentId,
  linkTarget,
}) => {
  const [isCopied, setIsCopied] = useState(false);
  const [isCopying, setIsCopying] = useState(false);

  const handleCopy = useCallback(async () => {
    if (active || studentId == null || isCopying) {
      return;
    }

    setIsCopying(true);

    try {
      let url: string;

      if (linkTarget === 'enviar-fotos') {
        const { token } = await StudentTokenService.getOrCreatePhotoUploadLink(studentId, 'body');
        url = LINK_BUILDERS[linkTarget](token);
      } else {
        const { token } = await StudentTokenService.generateToken(studentId);
        url = LINK_BUILDERS[linkTarget](token);
      }

      await navigator.clipboard.writeText(url);
      setIsCopied(true);
      window.setTimeout(() => setIsCopied(false), 2500);
    } catch (error) {
      alert(error instanceof Error ? error.message : COPY_ERROR_MESSAGES[linkTarget]);
    } finally {
      setIsCopying(false);
    }
  }, [active, isCopying, linkTarget, studentId]);

  if (active) {
    return (
      <div className={styles['form-status-cell']}>
        <StatusIndicator active label={activeLabel} />
      </div>
    );
  }

  if (studentId == null) {
    return (
      <div className={styles['form-status-cell']}>
        <StatusIndicator active={false} label={missingLabel} />
      </div>
    );
  }

  return (
    <div className={styles['form-status-cell']}>
      <Tooltip content={HOVER_HINT}>
        <button
          type="button"
          className={styles['form-status-copy-button']}
          onClick={() => {
            void handleCopy();
          }}
          disabled={isCopying}
          aria-label={`${missingLabel}. ${HOVER_HINT}`}
        >
          <span
            className={[
              styles['form-status-icon'],
              styles['form-status-icon-missing'],
            ].join(' ')}
            aria-hidden="true"
          >
            <span className="material-symbols-outlined">close</span>
          </span>
        </button>
      </Tooltip>
      {isCopied && (
        <span className={styles['form-status-copied']} role="status">
          Link copiado
        </span>
      )}
    </div>
  );
};

export default OnboardingFormStatusCell;
